/*** IMPORTS FROM imports-loader ***/
import hydrator from "@gjensidige/views-api/src/runtime/hydrator";
import Component from ".";

/* global hydrator, Component */

// create a map of what we have hydrated, share it on window
window.__HYDRATED__ = window.__HYDRATED__ || {};

// get the current script executing
const partSrc = window.document?.currentScript?.getAttribute('src');

// filter the ones that need to be hydrated (data-nodeid contains the id for the props -> only hydration on node id's)
const parts = Array.from(
  document.querySelectorAll('script[src="' + partSrc + '"]')
).filter((part) => part.getAttribute('data-nodeid'));

// if this selection of parts contains elements that needs to be hydrated, add an eventListener
if (parts.length && window.addEventListener) {
  window.addEventListener('load', function () {
    parts.forEach((part) => {
      // make sure we only hydrate our components once
      const NODE_ID = part.getAttribute('data-nodeid');
      if (!window.__HYDRATED__[NODE_ID]) {
        window.__HYDRATED__[NODE_ID] = true;
        hydrator(Component, NODE_ID);
      }
    });
  });
}


import React, { useEffect, useRef, useState } from 'react';
import { NavMenu, NavProps, NavUserResponse } from '@internal/schema-client';
import { canUseDOM } from '@internal/utils/can-use-dom';
import superagent from 'superagent';
import { Nav } from '@gjensidige/core-navigation';
import { SystemMessages } from '@gjensidige/core-navigation/lib-css';
import { Lock } from '@gjensidige/nci-core-icons/lib/lock';

import { ApiToNavItems } from './apiToNavItems';
import { CookieConsent } from './cookie-consent/consent';
import { SearchButton } from './search/button';

import styles from '../less/nav.module.less';

export const Navigation = ({
  userApiUrl = '/_/service/com.gjensidige.builders.theme/api-user',
  baseUrl = '/',
  menuData,
  currentPath = '/'
}: NavProps) => {
  const ref = useRef(null);
  const { actions = [], items = [], language = 'en', search } = menuData || {};
  const [notifications, setNotifications] = useState({});
  const [systemMessages, setSystemMessages] = useState([]);
  const [userData, setUserData] = useState<NavUserResponse>({
    isAuthorized: false
  });

  // Recursively set isExpanded to items, but also actions to prevent item highlight if current url is action
  addIsExpandedToItems(currentPath, [...items, ...actions]);

  // Allow positon: sticky to properly function
  useEffect(() => {
    if (ref.current) ref.current.parentElement.className = styles.wrapper;
  }, []);

  useEffect(() => {
    // TMP fix for log out clearing sessionstorage
    if (window.location.pathname === '/utlogget') {
      try {
        window.sessionStorage.removeItem('userData');
      } catch (err) {
        // No sessionStorage
      }
      return;
    }

    const sessionUser = getSessionStorageOrDefault('userData', userData);
    const updateUserData = async () => {
      try {
        const res = await superagent
          .get(userApiUrl)
          .accept('application/json')
          .set('Applikasjon', 'INTERNETT')
          .send();
        setUserData(normalizeUserResponse(res));
      } catch (err) {
        console.log('Error fetching user-data');
      }
    };
    if (sessionUser.isAuthorized) setUserData(sessionUser);
    else updateUserData();
    writePartnerToSessionStorage();
  }, []);

  useEffect(() => {
    if (userData.isAuthorized) {
      if (window && window.sessionStorage) {
        sessionStorage.setItem('userData', JSON.stringify(userData));
      }
      if (menuData.notifications?.length > 0) {
        menuData.notifications.forEach((request) =>
          getNotifications(request).then((nextNotifications) => {
            setNotifications((prevNotifications) => ({
              ...prevNotifications,
              ...nextNotifications
            }));
          })
        );
      }
    }
  }, [userData]);

  return (
    <>
      {canUseDOM() && <CookieConsent language={language} />}
      {canUseDOM() && (
        <SystemMessages
          systemMessages={systemMessages}
          setSystemMessages={setSystemMessages}
          apiEndpoint={`${baseUrl}api-builders/system-messages`}
          groupFilter={'system-messages'}
        />
      )}
      <Nav
        className={styles.nav}
        homeUrl={menuData.homeUrl}
        isLoggedIn={false}
        language={language}
        ref={ref}
        skipId="content"
      >
        <ApiToNavItems
          currentPath={currentPath}
          homeUrl={menuData.homeUrl}
          items={items}
          notifications={notifications}
        />
        <SearchButton {...search} />
        <Nav.Actions>
          <Nav.Cart />
          <ApiToNavItems currentPath={currentPath} items={actions} />
        </Nav.Actions>
        {userData.isAuthorized ? (
          <Nav.User
            label={userData.fullname}
            href={menuData.authentication?.login} // TMP fix to send user to profile page
          />
        ) : (
          <Nav.User icon={<Lock />} href={menuData.authentication?.login} />
        )}
      </Nav>
    </>
  );
};

Navigation.CSR = true;
Navigation.getServerSideProps = async function ({ menuApiUrl }) {
  if (!menuApiUrl) {
    console.error(
      'missing required menuApiUrl prop set by NAV_API_URL env var'
    );
  }

  const res = await superagent
    .get(menuApiUrl)
    .accept('application/json')
    .send();

  return { menuData: res.body };
};

export default Navigation;

// Utilities
function getSessionStorageOrDefault(key: string, defaultValue) {
  try {
    return JSON.parse(window.sessionStorage.getItem(key)) || defaultValue;
  } catch (err) {
    return defaultValue;
  }
}

function normalizeUserResponse(res: superagent.Response): NavUserResponse {
  const userResponse = {
    isAuthorized: false,
    fullname: '',
    customerType: ''
  };

  if (res.statusCode !== 200) return userResponse;

  // Normalize api-user/v1 used by NO
  try {
    if (res.body?.personalia && res.body?.identifikasjon) {
      const { partref } = res.body.identifikasjon;
      const { fornavn, etternavn } = res.body.personalia;
      if (fornavn && etternavn && partref) {
        return {
          isAuthorized: true,
          fullname: `${fornavn} ${etternavn}`,
          customerType: partref
        };
      }
    }
  } catch (err) {
    console.log(`failed to normalize api-user/v1 response`);
  }

  // Normalize web-api-sweden/customer for SE and api/user for DK
  try {
    const { name: fullname, customerType } = res.body;
    if (fullname) {
      return {
        isAuthorized: true,
        fullname,
        customerType
      };
    }
  } catch (err) {
    console.log(`failed to normalize web-api-sweden/customer response`);
  }
  return userResponse;
}

async function getNotifications(request: NavMenu['notifications'][0]) {
  const headers = {};
  const headersArray = [].concat(request.headers || []);
  headersArray.forEach(({ key, value }) => (headers[key] = value));

  try {
    return await fetch(request.endpoint, {
      method: 'GET',
      credentials: 'include',
      headers
    }).then((response) => response.json());
  } catch (e) {
    console.log(`Error fetching notifications from: ${request.endpoint}`);
    return {};
  }
}

function addIsExpandedToItems(currentPath: string, items: NavMenu['items']) {
  // Last item in the collection containing the path should be current, assuming shorter urls are earlier in order
  const hrefs = items.map(({ href }) => href).filter(Boolean);
  const hrefsByAscendingLength = hrefs.sort((a, b) => a.length - b.length);
  const currentLevelPath = hrefsByAscendingLength.reduce((acc, href) => {
    return currentPath.startsWith(href) ? href : acc;
  }, currentPath);

  // Add highlight to items that either contain the current item, or the current url
  return items?.some((item) => {
    return (item.isExpanded =
      addIsExpandedToItems(currentPath, item.children) ||
      currentLevelPath === item.href);
  });
}

//special functionality for sales norway, adding partner to session storage
function writePartnerToSessionStorage() {
  try {
    const partner = new URLSearchParams(location.search).get('partner');
    if (partner)
      sessionStorage.setItem(
        'nettbutikk.partner',
        JSON.stringify({
          partner: partner,
          source: 'PARAM'
        })
      );
  } catch (err) {
    // sessionStorage is full or URLSearchParams is not supported
  }
}
